import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, apiTokenRequest } from '@/lib/auth/entra/msal';
import { loadPinSession } from '@/lib/auth/pin/session';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * Acquire a bearer token for the API. Two paths:
 *   - Tier-1 (manager): MSAL silent token acquire, with redirect fallback on
 *     InteractionRequired.
 *   - Tier-2 (PIN):    read the PIN-session token from sessionStorage.
 *
 * If both are present, MSAL wins — managers are higher-privilege and we don't
 * mix tiers in a single tab. If neither is present, returns null and the
 * request goes anonymous (caller's responsibility to handle 401).
 */
async function getAccessToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (account) {
    try {
      const result = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect(apiTokenRequest);
        return null;
      }
      throw err;
    }
  }

  const pin = loadPinSession();
  if (pin) return pin.token;

  return null;
}

export interface ApiFetchOptions extends RequestInit {
  /** Skip bearer-token attachment (for anonymous endpoints like /health). */
  anonymous?: boolean;
}

export async function apiFetch<T>(path: string, init: ApiFetchOptions = {}): Promise<T> {
  const { anonymous, headers: extraHeaders, ...rest } = init;
  const correlationId = crypto.randomUUID();
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    'x-correlation-id': correlationId,
    ...(extraHeaders as Record<string, string> | undefined),
  };

  if (!anonymous) {
    const token = await getAccessToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { ...rest, headers });
  if (!res.ok) {
    let detail = '';
    try {
      detail = await res.text();
    } catch {
      // ignore
    }
    throw new Error(
      `API ${res.status} ${res.statusText} (correlation_id=${correlationId})${detail ? ` — ${detail}` : ''}`,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
